import {
  SSO_ACK_MESSAGE_SOURCE,
  SSO_ACK_TIMEOUT_MS,
  SSO_ATTEMPT_MARKER_KEY,
  SSO_MESSAGE_SOURCE,
  SSO_TICKET_CLAIM_PREFIX,
  SSO_TICKET_CLAIM_TTL_MS
} from '@constants/sso';

import { SsoBootstrapParams } from '@services/api/auth/ssoTypes';

/** Shape the popup hands to its opener. */
export interface SsoBridgeMessage extends SsoBootstrapParams {
  source: typeof SSO_MESSAGE_SOURCE;
}

/** True when this window was opened by another one — i.e. it may be the SSO popup. */
export const hasOpener = (): boolean => Boolean(window.opener) && window.opener !== window;

/**
 * Runs the popup side of the SSO handshake.
 *
 * Hands the `sso_ticket` / `sso_error` from the backend redirect to the opener
 * and waits for its acknowledgement. Resolves `true` once acknowledged (the
 * window is closing, the opener owns the exchange from here), `false` when
 * nothing answers within SSO_ACK_TIMEOUT_MS.
 *
 * The acknowledgement is not a nicety — it is the safety interlock. Any window
 * opened via target="_blank" without rel="noopener" also has a `window.opener`,
 * so closing on send alone could shut a user's main window. No ack means no
 * close: the caller then finishes the sign-in in this very window (the ticket is
 * still valid — it lives 60 seconds).
 */
export const requestOpenerTakeover = (params: SsoBootstrapParams): Promise<boolean> =>
  new Promise(resolve => {
    const opener = window.opener as Window | null;

    if (!opener) {
      resolve(false);

      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      window.removeEventListener('message', onAck);
      clearTimeout(timeoutId);
    };

    const onAck = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.source !== SSO_ACK_MESSAGE_SOURCE) {
        return;
      }

      cleanup();
      window.close();
      resolve(true);
    };

    window.addEventListener('message', onAck);

    timeoutId = setTimeout(() => {
      cleanup();
      resolve(false);
    }, SSO_ACK_TIMEOUT_MS);

    const message: SsoBridgeMessage = { source: SSO_MESSAGE_SOURCE, ...params };
    opener.postMessage(message, window.location.origin);
  });

/**
 * Claims the one-time ticket for THIS window before it is exchanged.
 *
 * Why: the ack handshake has a deliberate timeout — after SSO_ACK_TIMEOUT_MS
 * the popup assumes nobody is listening and finishes the sign-in itself. But
 * the postMessage may already be in flight to an opener that is merely slow
 * (background-tab throttling, a long GC). Both windows would then POST the
 * single-use ticket, and the loser shows a bogus "link is no longer valid"
 * failure. The claim closes that race: each side claims before exchanging, and
 * the loser backs off silently.
 *
 * localStorage rather than module state because the race is between two
 * same-origin WINDOWS — module state is per-window. Claims self-expire after
 * the ticket's own lifetime. When storage is unavailable (private mode,
 * blocked site data) the claim degrades to "granted": the guard protects a
 * narrow race, and refusing to sign in at all would be the worse failure.
 */
/**
 * Records that this browsing context deliberately started an SSO sign-in.
 *
 * Written at click time, BEFORE the popup opens: `window.open` snapshots the
 * opener's sessionStorage into the new window, so both the popup and the
 * same-tab-redirect fallback carry the marker through the round trip. A ticket
 * arriving in a context WITHOUT the marker was not requested from here — a
 * mailed link, a bookmark — and must not be exchanged silently (login CSRF).
 *
 * sessionStorage, not localStorage: the marker must die with the tab and must
 * not leak the "an SSO attempt is running" bit across unrelated windows.
 */
export const markSsoAttemptStarted = (): void => {
  try {
    sessionStorage.setItem(SSO_ATTEMPT_MARKER_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — hasSsoAttemptMarker degrades to "granted" too.
  }
};

/** Removes the marker once the attempt is finished (either way). */
export const clearSsoAttemptMarker = (): void => {
  try {
    sessionStorage.removeItem(SSO_ATTEMPT_MARKER_KEY);
  } catch {
    // Nothing to clean when storage is unavailable.
  }
};

/**
 * True when this context (or its opener, via the sessionStorage copy) started
 * the SSO attempt. Degrades to "granted" when storage is unavailable — the
 * marker guards against silent sign-ins from crafted links, and a browser
 * with all site data blocked cannot hold a session anyway.
 */
export const hasSsoAttemptMarker = (): boolean => {
  try {
    return sessionStorage.getItem(SSO_ATTEMPT_MARKER_KEY) !== null;
  } catch {
    return true;
  }
};

export const claimSsoTicket = (ticket: string): boolean => {
  try {
    const now = Date.now();

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);

      if (!key?.startsWith(SSO_TICKET_CLAIM_PREFIX)) {
        continue;
      }

      const claimedAt = Number(localStorage.getItem(key));

      if (!Number.isFinite(claimedAt) || now - claimedAt > SSO_TICKET_CLAIM_TTL_MS) {
        localStorage.removeItem(key);
      }
    }

    const claimKey = `${SSO_TICKET_CLAIM_PREFIX}${ticket}`;

    if (localStorage.getItem(claimKey) !== null) {
      return false;
    }

    localStorage.setItem(claimKey, String(now));

    return true;
  } catch {
    return true;
  }
};
